import axios from "axios";
import appConfig from "../appConfig";
import { APIHeaders } from "./api.service"; // Import the APIHeaders class
import { Helper, HelperConfig, HelperRedirect, HelperWebView } from "./helper/helper.service";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import { AppStorage } from "../storage/app-storage";
import { ModalLabels } from "../../assets/labels/labels";

interface AuthResponse {
  http_code?: number;
  error_http_code?: number;
  code?: number;
  message?: string;
  data?: AuthResponseData;
  type?: string;
}
interface AuthResponseProvider {
  http_code?: number;
  code?: number;
  message?: string;
  token?: string;
  redirect_type?: RedirectType;
  company?: string;
}

interface AuthResponseData {
  token?: string;
  redirect_type?: RedirectType;
  company?: string;
  user_id?: string;
  work_token?: string;
  work_email?: string;
  work_phone?: string;
  message?: string;
  is_cal_token?: string;
}

interface CompanyIAgreeDataData {
  name?: string;
  vat?: string;		
  client?: string[];
  item?: string[];
  work?: string[];
  terms?: string;
}

interface CompanyIAgreeData {
  type: string;
  data: CompanyIAgreeDataData;
}

export enum RedirectType {
  BV = "BV",
  Companies = "COMPANIES",
  Vertical = "VERTICAL"
}

export class AuthenticationService {
  static cookies = new Cookies();
  static open_appointment = this.cookies.get("openAppointment");
  static domain = this.cookies.get("kssreturncompany")?.replace(".qa", "");

  static async postProviderUser(body: any) {
    try {
      const isEmbedded = Helper.isEmbedded();
      if (isEmbedded) {
        body.embedded = true;
      }
      const response = await axios.post(`${appConfig.BASE_URL}/user/login/provider`, body, {
        headers: APIHeaders.fetchHeaders,
      });
      if(response?.data?.http_code===409 && response?.data?.code===4) {
        return response.data;
      }
      return response?.data?.data;
    } catch (error) {
      // Handle the error, e.g., logging it or returning an empty array
      console.error("Error:", error);
      return [];
    }
  }

  static async postSignUpUser(body: any) {
    try {
      const isEmbedded = Helper.isEmbedded();
      if (isEmbedded) {
        body.embedded = true;
      }
      const response = await axios.post(`${appConfig.BASE_URL}/user/signup/`, body, {
        headers: APIHeaders.fetchHeaders,
      });
      return response?.data;
    } catch (error) {
      // Handle the error, e.g., logging it or returning an empty array
      console.error("Error:", error);
      return {};
    }
  }


  static async postRequestPhoneCode(body: any) {
    try {
      const response = await axios.post(`${appConfig.BASE_URL}/phone/code/`, body, {
        headers: APIHeaders.fetchHeaders,
      });
      return response?.data;
    } catch (error) {
      // Handle the error, e.g., logging it or returning an empty array
      console.error("Error:", error);
      return {};
    }
  }


  
  static async postAuthenticateUserPhone(body: any) {
    try {
      const isEmbedded = Helper.isEmbedded();
      if (isEmbedded) {
        body.embedded = true;
      }
      const response = await axios.post(`${appConfig.BASE_URL}/user/login/phone/`, body, {
        headers: APIHeaders.fetchHeaders,
      });
      return response?.data;
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }

  static async postAuthenticateUser(body: any) {
    try {
      const isEmbedded = Helper.isEmbedded();
      if (isEmbedded) {
        body.embedded = true;
      }
      const response = await axios.post(`${appConfig.BASE_URL}/user/login/`, body, {
        headers: APIHeaders.fetchHeaders,
      });
      return response?.data;
    } catch (error) {
      // Handle the error, e.g., logging it or returning an empty array
      console.error("Error:", error);
      return {};
    }
  }

  static async postSendEmailConfirmation(body: any) {
    try {
      const isEmbedded = Helper.isEmbedded();
      if (isEmbedded) {
        body.embedded = true;
      }
      const response = await axios.post(`${appConfig.BASE_URL}/user/email/`, body, {
        headers: APIHeaders.fetchHeaders,
      });
      return response?.data;
    } catch (error) {
      // Handle the error, e.g., logging it or returning an empty array
      console.error("Error:", error);
      return {};
    }
  }

  static async getCompanyIAgreeData(domain: string): Promise<CompanyIAgreeData> {
    try {
      const response = await axios.get(`${appConfig.BASE_URL}/terms/company?value=${domain}`, {
        headers: APIHeaders.fetchHeaders,
      });
      return response.data;
    } catch (error) {
      console.error("Error:", error);
      // Adjust this to match the expected return type, e.g., returning a default object
      return { type: "Error", data: {} as CompanyIAgreeDataData };
    }
  }

  static async getEmailConfirmation(token: string, queryParams?: string) {
    const params =  queryParams || '';
    try {
      const response = await axios.get(`${appConfig.BASE_URL}/user/email/confirm?token=${token}${params}`, {
        headers: APIHeaders.fetchHeaders,
      });
      return response?.data;
    } catch (error) {
      // Handle the error, e.g., logging it or returning an empty array
      console.error("Error:", error);
      return {};
    }
  }

  static async postPassRecovery(body: any) {
    try {
      const response = await axios.post(`${appConfig.BASE_URL}/user/password/recover/`, body, {
        headers: APIHeaders.fetchHeaders,
      });
      return response?.data;
    } catch (error) {
      // Handle the error, e.g., logging it or returning an empty array
      console.error("Error:", error);
      return {};
    }
  }

  static async patchPassUpdate(body: { password: string }, token: string) {
    try {
      const response = await axios.patch(
        `${appConfig.BASE_URL}/user/password?token=${token}`,
        body,
        {
          headers: APIHeaders.fetchHeaders,
        }
      );
      return response?.data;
    } catch (error) {
      // Handle the error, e.g., logging it or returning an empty array
      console.error("Error:", error);
      return {};
    }
  }

  // ## API Handlers  #####################################
  static authenticateProviderUser = (data: any, navigate: ReturnType<typeof useNavigate>) => {
    let body: { email: string; password: string; user_id?: string; work_token?: string; domain?: string; work_email?: string; work_phone?: string; open_appointment?: boolean; is_cal_token?: string; terms_accepted?:boolean} = {
      ...data
    };
    if ( this.cookies.get("userId")) {
      body = {
        ...body,
        user_id: this.cookies.get("userId"),
        domain: this.domain,
        work_token: this.cookies.get("liteToken"),
        work_email: this.cookies.get("liteEmail"),
        work_phone: this.cookies.get("litePhone"),
        is_cal_token: this.cookies.get("isCalToken"),
      };
    } else {
      if (this.domain !== null) {
        body = {
          ...body,
          domain: this.domain,
        };
      }
    }
    if (this.open_appointment) {
      body = {
        ...body,
        open_appointment: this.open_appointment,
      }
    }
    return this.postProviderUser(body).then((response: AuthResponseProvider) => {
      if (response) {
        if (response.http_code) {
          if (response.http_code === 409 && response.code === 4) {
            return 409;
          } else {
            console.error("ERROR", response.message);
            return;
          }
        } else {
          const eventInfo = {
            auth_token: response?.token,
            route: this.cookies.get("route"),
            client_route: this.cookies.get("clientroute"),
          };
          if (HelperWebView.isIOSWebview()) {
            try {
              (window as any).webkit.messageHandlers.login.postMessage(eventInfo);
            } catch (error) {
              console.error(error);
            }
          }
          if (HelperWebView.isAndroidWebview()) {
            try {
              (window as any).Android.login(
                eventInfo.auth_token,
                eventInfo.route,
                eventInfo.client_route
              );
            } catch (error) {
              console.error(error);
            }
          }
          if (typeof response?.token === 'string') {
            Helper.setCookie("sessionToken", response.token);
          } else {
            Helper.setCookie("sessionToken", "");
          }
          if (response?.redirect_type) {
            this.handleAuthRedirect(response, navigate);
          }
        }
      } else {
        console.error("GENERIC ERROR");
        return this.renderGenericError();
      }
    });
  };

static requestPhoneCode = (
  body: { phone?: string, platform: string; terms_accepted?:boolean},
  navigate: ReturnType<typeof useNavigate>
) => {
  return this.postRequestPhoneCode(body).then((response: AuthResponse) => {
    if (response) {
      if (response.http_code) {
        if (response.http_code === 401) {
          return;
        } else if (response.http_code === 400 || response.http_code === 403) {
          return {
            title: "Dados Incorrectos",
            message: response.message || "",
            subMessage: "",
            onConfirm: "",
            confirmText: "Ok",
          };
        } else if (response.http_code === 409 && response.code === 4) {
          return {
            type: 409
          };
        } else {
          return this.renderGenericError();
        }
      } else {
        return {
          type: response.type || "",
          title: "SMS com código enviado.",
          message: response.data?.message || "",
          subMessage: "Introduza o código recebido",
          onConfirm: "",
          confirmText: "Ok",
        };
      }
    } else {
      return this.renderGenericError();
    }
  });
};

static authenticateUserPhone = (
  body: { user_id?: string; work_token?: string;
    domain?: string; work_email?: string; work_phone?: string; open_appointment?: boolean; is_cal_token?: string; phone?: string; code?: string;terms_accepted?:boolean },
  navigate: ReturnType<typeof useNavigate>
) => {
  return this.postAuthenticateUserPhone(body).then((response: AuthResponse) => {
    if (response) {
      if (response.http_code) {
        if (response.http_code === 401) {
          return;
        } else if (response.http_code === 400 || response.http_code === 403) {
          return {
            type: response.type || "",
            code: response.code || "",
            title: "Dados Incorrectos",
            message: response.message || "",
            subMessage: "",
            onConfirm: "",
            confirmText: "Ok",
          };
        } else if (response.http_code === 404) {
          if (response.code === 3) {
            return this.renderGenericError();
          }
        } else {
          return this.renderGenericError();
        }
      } else {
        const eventInfo = {
          auth_token: response.data?.token,
          route: this.cookies.get("route"),
          client_route: this.cookies.get("clientroute")
        };
        if (HelperWebView.isIOSWebview()) {
          try {
            (window as any).webkit.messageHandlers.login.postMessage(eventInfo);
          } catch (error) {
            console.error(error);
          }
        }
        if (HelperWebView.isAndroidWebview()) {
          try {
            (window as any).Android.login(
              eventInfo.auth_token,
              eventInfo.route,
              eventInfo.client_route
            );
          } catch (error) {
            console.error(error);
          }
        }
        if (typeof response.data?.token === 'string') {
          Helper.setCookie("sessionToken", response.data?.token);
        } else {
          Helper.setCookie("sessionToken", "");
        }

        if (response.data?.redirect_type) {
          this.handleAuthRedirect(response.data, navigate);
        }
      }
    } else {
        return this.renderGenericError();
    }
  });
};

static userLoggedIn = (token: any) => {
  return this.getUserLoggedIn(token).then((response: any) => {
    if (response) {
      if (response.http_code) {
        if (response.http_code === 401) {
          return { redirect_type: "401" };
        } else {
          return { redirect_type: "Error" };
        }
      } else {
        if (response.data) {
          return response.data;
        } 
      }
    } else {
        return { redirect_type: "Error" };
    }
  });
};

static async getUserLoggedIn(token: string) {
  try {
    const response = await axios.get(`${appConfig.BASE_URL}/user/redirect`, {
      headers: APIHeaders.fetchHeadersWithAuth(token),
    });
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    // Adjust this to match the expected return type, e.g., returning a default object
    return { redirect_type: "Error" };
  }
}

  static authenticateUser = (
    body: { email?: string; password?: string; user_id?: string; work_token?: string;
      domain?: string; work_email?: string; work_phone?: string; open_appointment?: boolean; is_cal_token?: string; phone?: string; code?: string },
    navigate: ReturnType<typeof useNavigate>
  ) => {
    return this.postAuthenticateUser(body).then((response: AuthResponse) => {
      if (response) {
        if (response.http_code) {
          if (response.http_code === 401) {
            return;
          } else if (response.http_code === 400 || response.http_code === 403) {
            return {
              title: "Dados Incorrectos",
              message: response.message || "",
              subMessage: "",
              onConfirm: "",
              confirmText: "Ok",
            };
          } else if (response.http_code === 409) {
            if (response.code === 1) {
              return {
                title: "E-mail por Confirmar",
                message: "O seu e-mail ainda não foi confirmado.\n**Continue o registo através do e-mail que lhe reenviámos.**\nObrigado.",//response.message || "",
                subMessage: "",
                onConfirm: "",
                confirmText: "Ok",
              };
              // this.renderInfoModal('Registo Incompleto', response.message);
            }
          } else if (response.http_code === 404) {
            if (response.code === 3) {
              return this.renderGenericError();
              // this.renderInfoModal(Labels.genericErrorTitle, response.message);
            }
          } else {
             return this.renderGenericError();
          }
        } else if (response.data?.token) {
          const eventInfo = {
            auth_token: response.data?.token,
            route: this.cookies.get("route"),
            client_route: this.cookies.get("clientroute")
          };
          if (HelperWebView.isIOSWebview()) {
            try {
              (window as any).webkit.messageHandlers.login.postMessage(eventInfo);
            } catch (error) {
              console.error(error);
            }
          }
          if (HelperWebView.isAndroidWebview()) {
            try {
              (window as any).Android.login(
                eventInfo.auth_token,
                eventInfo.route,
                eventInfo.client_route
              );
            } catch (error) {
              console.error(error);
            }
          }
          if (typeof response.data?.token === 'string') {
            Helper.setCookie("sessionToken", response.data.token);
          } else {
            Helper.setCookie("sessionToken", "");
          }
          if (response.data?.redirect_type) {
            this.handleAuthRedirect(response.data, navigate);
          }
        }
      } else {
          return this.renderGenericError();
      }
    });
  };

  static signUpUser = (
    body: { email: string; password: string; user_id?: string; work_token?: string; domain?: string; work_email?: string; work_phone?: string; terms_accepted?:boolean},
    navigate: ReturnType<typeof useNavigate>,
    isEmbedded: boolean = false
  ) => {
    return this.postSignUpUser(body).then((response: AuthResponse) => {
      if (response && Object.keys(response).length > 0) {
        if (response.http_code) {
          if (response.http_code === 401) {
            return;
          } else if (response.http_code === 400) {
            return {
              title: "Dados Incorretos",
              message: response.message || "",
              subMessage: "",
              onConfirm: "",
              confirmText: "Ok",
            };
            // this.renderInfoModal('Dados Incorrectos', response.message);
          } else if (response.http_code === 409) {
            if (response.code === 1) {
              // console.error('(Modal) Em falta: ', response.message);
              return {
                title: "E-mail por Confirmar",
                message: "O seu e-mail ainda não foi confirmado.\n**Continue o registo através do e-mail que lhe reenviámos.**\nObrigado.",//response.message || "",
                subMessage: "",
                onConfirm: "",
                confirmText: "Ok",
              };
            } else if (response.code === 3) {
              return {
                title: "Utilizador já Existente",
                message: response.message || "",
                subMessage: "",
                onConfirm: "",
                confirmText: "Ok",
              };
            } else if (response.code === 4) {
              return {
                title: "Aceitar Política de Privacidade",
                message: "Por favor aceite a Política de Privacidade",
                subMessage: "",
                onConfirm: "",
                confirmText: "Ok",
              };
            }
          } else if (response.http_code === 404) {
            if (response.code === 3) {
              return this.renderGenericError();
            }
          } else {
            return this.renderGenericError();
          }
        } else {
          let state: {
            email: string;
            password: string;
            userId?: string;
            liteToken?: string;
            liteEmail?: string;
            litePhone?: string;
          } = {
            email: body.email,
            password: body.password,
          };
          if (response.data?.user_id) {
            this.cookies.set(
              "liteToken",
              response.data?.work_token,
              HelperConfig.cookiesLiteConfig
            );
            this.cookies.set(
              "liteEmail",
              response.data?.work_email,
              HelperConfig.cookiesLiteConfig
            );
            this.cookies.set(
              "litePhone",
              response.data?.work_phone,
              HelperConfig.cookiesLiteConfig
            );
            this.cookies.set(
              "userId",
              response.data?.user_id,
              HelperConfig.cookiesLiteConfig);
            state = {
              ...state,
              userId: response.data?.user_id,
              liteToken: response.data?.work_token,
              liteEmail: response.data?.work_email,
              litePhone: response.data?.work_phone,
            };
          }
          navigate(`/send-confirm-email${isEmbedded ? '?embedded=true' : ''}`, { state });

        }
      } else {
        return this.renderGenericError();
      }
    });
  };

  static sendEmailConfirmation = (
    body: { email: string; user_id?: string; work_token?: string; work_email?: string; work_phone?: string; is_cal_token?: string; embedded?:string;},
    navigate: ReturnType<typeof useNavigate>,
    resend?: boolean,
    isEmbedded?: string
  ) => {
    return this.postSendEmailConfirmation(body).then((res: AuthResponse) => {
      if (res?.http_code === undefined || res?.http_code === 200) {
        if (res.data?.user_id) {
          this.cookies.set("liteToken", res.data?.work_token, HelperConfig.cookiesLiteConfig);
          this.cookies.set("userId", res.data?.user_id, HelperConfig.cookiesLiteConfig);
          this.cookies.set("liteEmail", res.data?.work_email, HelperConfig.cookiesLiteConfig);
          this.cookies.set("litePhone", res.data?.work_phone, HelperConfig.cookiesLiteConfig);
          this.cookies.set("isCalToken", res.data?.is_cal_token, HelperConfig.cookiesLiteConfig);
        }
        if (resend) {
          return {
            title: res.data?.message || "E-mail não foi enviado com sucesso.",
            message: "",
            subMessage: "",
            onConfirm: "navigate",
            confirmText: "Ok",
          };
        } else {
          const state = {
            email: body.email,
           // password: body.password,
            userId: this.cookies.get("userId"),
            liteToken: this.cookies.get("liteToken"),
            liteEmail: this.cookies.get("liteEmail"),
            litePhone: this.cookies.get("litePhone"),
            isCalToken: this.cookies.get("isCalToken"),
          };
      
          navigate(`/send-confirm-email${isEmbedded ? '?embedded=true' : ''}`, { state });
        }
      } else if (res.http_code === 404) {
        if (res.code === 3) {
          return this.renderGenericError();
        }
      } else {
        return {
          title: "Dados Incorretos",
          message: res.message || "",
          subMessage: "",
          onConfirm: "",
          confirmText: "Ok",
        };
      }
    });
  };

  static serviceGetCompanyIAgreeData(domain: string): Promise<CompanyIAgreeDataData | null> {
    return this.getCompanyIAgreeData(domain).then((res: CompanyIAgreeData) => {
      if (res) {
        return res.data;
      }
      return null;
    });
  }

  static submitEmailConfirmation = (token: string, params?: string) => {
    return this.getEmailConfirmation(token, params).then((res: AuthResponse) => {
      if (res) {
        if (res.http_code) {
          return {
            message: res?.message,
            path: "/send-confirm-email",
            modal: false,
            tokenError : true,
            code: res?.code,
          };
        } else {
          return {
            modal: false,
            tokenError : false, 
            token: res.data?.token,
            res: res,
          };
        }
      } else {
        return { ...this.renderGenericError(), modal: true, tokenError: true };
      }
    });
  };

  static passwordRecovery = (body: { email: string }, navigate: ReturnType<typeof useNavigate>) => {
    return this.postPassRecovery(body).then((res: AuthResponse) => {
      if (res) {
        if (res.http_code) {
          if (res.http_code === 401) {
            return;
          } else if (res.http_code === 400) {
            return {
              title: "Dados Incorrectos",
              message: res.message || ModalLabels.genericErrorMessage,
              subMessage: "",
              onConfirm: "",
              confirmText: "Ok",
            };
            // this.renderInfoModal('Dados Incorrectos', res.message, );
          } else if (res.http_code === 409) {
            return {
              title: "'Conta Inválida'",
              message: res.message || ModalLabels.genericErrorMessage,
              subMessage: "",
              onConfirm: "",
              confirmText: "Ok",
            };
            // this.renderInfoModal('Conta Inválida', res.message);
          } else if (res.http_code === 202) {
            navigate("/send-email-password", { state: { email: body?.email } });
            // this.props.history.push({ pathname: `/send-email-password`, state: { email: this.state.email } });
          } else if (res.http_code === 404) {
            if (res.code === 3) {
              return this.renderGenericError();
              // this.renderInfoModal(Labels.genericErrorTitle, res.message);
            }
          } else {
            return this.renderGenericError();
          }
        } else {
          navigate("/send-email-password", { state: { email: body?.email, emailSent: true } });
        }
      } else {
        return this.renderGenericError();
      }
    });
  };
  static passwordUpdate = (
    body: { password: string },
    token: string
  ) => {
    return this.patchPassUpdate(body, token).then((res: AuthResponse) => {
      if (res) {
        if (res.http_code) {
          if (res.http_code === 401) {
            return;
          } else if (res.http_code === 400) {
            if (res.error_http_code === 3) {
              return this.renderGenericError(undefined, res.message);
            }
            return {
              title: "Dados Incorrectos",
              message: res.message || ModalLabels.genericErrorMessage,
              subMessage: "",
              onConfirm: "",
              confirmText: "Ok",
            };
          } else if (res.http_code === 404) {
            return {
              title: "Informação Incorreta",
              message: "O link de recuperação de password não está correto. \n\nCarregue em continuar para pedir um novo link.",
              subMessage: "",
              onConfirm: "goToRecoverPassword",
              confirmText: "Continuar",
            };
            // this.renderInfoModal('Conta Inválida', res.message);
          } else if (res.http_code === 202) {
            return {
              title: "Password Alterada",
              message: "Password alterada com sucesso!",
              subMessage: "",
              onConfirm: "onLogin",
              confirmText: "Ok",
            };
          } else if (res.http_code === 409) {
            return {
              title: "Password não alterada",
              message: "O link de recuperação de password já não está válido. \n\nFoi enviado um link atualizado para o seu e-mail.\nContinue a recuperação de password através do novo link.",
              subMessage: "",
              onConfirm: "onLogin",
              confirmText: "Ok",
            };
          } else {
            return this.renderGenericError();
          }
        } else {
          return {
            title: "Password Alterada",
            message: "Password alterada com sucesso!",
            subMessage: "",
            onConfirm: "onLogin",
            confirmText: "Ok",
          };
        }
      } else {
        return this.renderGenericError();
      }
    });
  };

  static renderGenericError = (title?: string, message?: string) => {
    return {
      title: title || ModalLabels.genericErrorTitle,
      message: message || ModalLabels.genericErrorMessage,
      subMessage: "",
      onConfirm: "",
      confirmText: "Ok",
      type: "Error",
      code: "Error"
    };
  };

  static handleAuthRedirect = (
    data: AuthResponseData,
    navigate: ReturnType<typeof useNavigate>
  ) => {
    if (this.cookies.get("kssreturncompany")) {
      const isRegisterPage = Helper.isRegisterPage(window.location.pathname);
      const isConfirmEmailPage = Helper.isConfirmEmailPage(window.location.pathname);

      const isEmbedded = Helper.isEmbedded();
      if (!isEmbedded) {
        HelperRedirect.goToAppClient(
          this.cookies.get("kssreturncompany"), this.cookies.get("openAppointment"), isRegisterPage || isConfirmEmailPage);
      } else {
        const returnUrl = this.cookies.get("redirectAuth");
        if (returnUrl) {
          HelperRedirect.goToAppClient(
                this.cookies.get("kssreturncompany"), '' , isRegisterPage || isConfirmEmailPage, returnUrl);
        } else {
          HelperRedirect.goToAppClient(
            this.cookies.get("kssreturncompany"), '', isRegisterPage || isConfirmEmailPage);
        }       
      }
    } else if (data?.company?.includes('null')) {
      navigate("/");
    } else {
      switch (data?.redirect_type) {
        case RedirectType.Companies:
          HelperRedirect.showCompaniesClient(data?.company || "checkpoint");
          break;

        case RedirectType.Vertical:
          AppStorage.authState = "LOGGEDIN";
          navigate("/");
          break;

        default:
          // data?.company &&
          HelperRedirect.goToAppClient(data?.company || "checkpoint");
          break;
      }
    }
  };

  static fetchCluster = () => {
    try {
      axios.get(`${appConfig.BASE_CLUSTER_URL}/mgmt/route`);
    } catch (error) {
      console.error("Error:", error);
    }
  };
}
